/**
 * Integrytas Group - Portal Framework (PFW)
 * Page: PFW.MEMBERSHIP
 *
 * Company self-registration + membership approvals. External users register a
 * company (pfw_register_company); company admins approve/reject pending members
 * (pfw_approve_member). Domain-matched users auto-join when auto_accept is on.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { hasAccess, type AuthSession } from '../../types/auth';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Search } from 'lucide-react';

// §17.B11: labelled constant for membership status codes (CHECK enum).
const STATUS_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  PENDING:  { labelKey: 'pfw_members.status.PENDING',  label: 'Pending',  variant: 'warning' },
  APPROVED: { labelKey: 'pfw_members.status.APPROVED', label: 'Approved', variant: 'success' },
  REJECTED: { labelKey: 'pfw_members.status.REJECTED', label: 'Rejected', variant: 'danger'  },
};

interface PendingRow {
  pfw_member_id: number; user_id: number; member_role: string; status: string;
  pfw_company: { internal_code: string; name: string } | null;
}

const PENDING_GRID_COLS: GridColumnDef[] = [
  { key: 'company_code', header: 'Company',     defaultWidth: 200 },
  { key: 'company_name', header: 'Company Name', defaultWidth: 240 },
  { key: 'member_role',  header: 'Role',         defaultWidth: 140 },
  { key: 'status',       header: 'Status',       defaultWidth: 120 },
];

export interface PfwMembershipPageProps { session: AuthSession }

export function PfwMembershipPage({ session }: PfwMembershipPageProps) {
  return (
    <PersonalizationProvider pageCode="PFW.MEMBERSHIP" session={session}>
      <PfwMembershipPageInner session={session} />
    </PersonalizationProvider>
  );
}


const PAGE_CODE = 'PFW.MEMBERSHIP';
function PfwMembershipPageInner({ session }: PfwMembershipPageProps) {
  // §6/§17: the write below is page-scoped, so the affordance follows
  // csm_role_page_access. The server refuses it regardless; this stops the
  // page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets for the list's logical columns.
  const [facets, setFacets] = useState<{ role: string[]; status: string[]; company: string[] }>(
    { role: [], status: [], company: [] },
  );
  // Register-company form
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [auto, setAuto] = useState(true);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('pfw_member')
        .select('pfw_member_id, user_id, member_role, status, pfw_company(internal_code, name)')
        .eq('status', 'PENDING');
      if (e) throw e;
      setPending((data ?? []) as PendingRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      role:    uniq(pending.map(m => m.member_role)).map(v => ({ value: v, label: v })),
      status:  uniq(pending.map(m => m.status)).map(v => ({ value: v, label: v })),
      company: uniq(pending.map(m => m.pfw_company?.internal_code ?? '')).map(v => ({ value: v, label: v })),
    };
  }, [pending]);

  const facetsActive = facets.role.length + facets.status.length + facets.company.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ role: [], status: [], company: [] }); };

  const detail = useExpandedRows<number>();
  const memberById = useMemo(() => new Map(pending.map(m => [m.pfw_member_id, m])), [pending]);

  // §17.B13 related-children (expand/contract). A pending member's parent company
  // is already loaded (with its resolved name), so surface it as a related record.
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => {
    const m = memberById.get(id);
    return [
      {
        key: 'company',
        title: t('pfw_members.related.company', 'Company'),
        items: m?.pfw_company
          ? [{ id: m.pfw_company.internal_code, label: m.pfw_company.name, title: m.pfw_company.internal_code }]
          : [],
      },
    ];
  };
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // A row passes a facet when no values are selected, or it matches one (OR within
    // a facet, AND across facets).
    const matchesFacets = (m: PendingRow) =>
      (facets.role.length    === 0 || facets.role.includes(m.member_role)) &&
      (facets.status.length  === 0 || facets.status.includes(m.status)) &&
      (facets.company.length === 0 || facets.company.includes(m.pfw_company?.internal_code ?? ''));
    return pending
      .filter(m => !q ||
        (m.pfw_company?.internal_code ?? '').toLowerCase().includes(q) ||
        (m.pfw_company?.name ?? '').toLowerCase().includes(q) ||
        (m.member_role ?? '').toLowerCase().includes(q))
      .filter(matchesFacets);
  }, [pending, filter, facets]);

  const decide = async (memberId: number, decision: 'APPROVE' | 'REJECT') => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const { data, error: e } = await apiDb.rpc('pfw_approve_member', { p_member_id: memberId, p_decision: decision });
      if (e) throw e;
      const res = data as { ok: boolean; error_code?: string; status?: string };
      if (!res?.ok) throw new Error(res?.error_code ?? 'PFW.APPROVE_FAILED');
      setNotice(t('pfw_members.notice.decided', 'Member {{id}}: {{status}}.', { id: memberId, status: res.status }));
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const register = async () => {
    setBusy(true); setError(null); setNotice(null);
    try {
      if (!code.trim() || !name.trim() || !domain.trim()) throw new Error(t('pfw_members.error.required_fields', 'Code, name, and email domain are required.'));
      const { data, error: e } = await apiDb.rpc('pfw_register_company', {
        p_code: code.trim().toUpperCase(), p_name: name.trim(), p_email_domain: domain.trim().toLowerCase(), p_auto_accept: auto ? 'Y' : 'N',
      });
      if (e) throw e;
      const res = data as { ok: boolean; error_code?: string; status?: string };
      if (!res?.ok) throw new Error(res?.error_code ?? 'PFW.REGISTER_FAILED');
      setNotice(t('pfw_members.notice.registered', 'Company registered (status {{status}}). A system administrator will activate it.', { status: res.status }));
      setCode(''); setName(''); setDomain('');
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('pfw_members.title', 'Portal Membership')}
          description={t('pfw_members.list.description', 'Register a company and approve or reject people requesting to join a portal.')}
          actions={
            <button onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> {t('pfw_members.refresh', 'Refresh')}
            </button>
          }
        />

        {error && (
          <ErrorBanner
            message={t('pfw_members.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <div className="max-w-2xl rounded border border-csm-border bg-white p-3">
          <div className="mb-2 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('pfw_members.register.heading', 'Register a Company')}</div>
          <div className="grid grid-cols-2 gap-2">
            <PersonalizedField componentKey="modal" fieldKey="code" label={t('pfw_members.field.company_code', 'Company Code')}>
              <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="COMPANY_CODE" className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" />
            </PersonalizedField>
            <PersonalizedField componentKey="modal" fieldKey="name" label={t('pfw_members.field.company_name', 'Company Name')}>
              <input value={name} onChange={e => setName(e.target.value)} placeholder={t('pfw_members.placeholder.company_name', 'Company name')} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" />
            </PersonalizedField>
            <PersonalizedField componentKey="modal" fieldKey="domain" label={t('pfw_members.field.email_domain', 'Email Domain')}>
              <input value={domain} onChange={e => setDomain(e.target.value.toLowerCase())} placeholder={t('pfw_members.placeholder.email_domain', 'email domain e.g. acme.com')} className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" />
            </PersonalizedField>
            <PersonalizedField componentKey="modal" fieldKey="auto_accept" label={t('pfw_members.field.auto_accept', 'Auto-accept this domain')}>
              <label className="flex h-8 items-center gap-2 text-csm-sm text-csm-ink-soft"><input type="checkbox" checked={auto} onChange={e => setAuto(e.target.checked)} className="h-4 w-4 accent-gray-700" /> {t('pfw_members.field.auto_accept', 'Auto-accept this domain')}</label>
            </PersonalizedField>
          </div>
          <div className="mt-2 flex justify-end">
            {canAdd && (<button onClick={() => void register()} disabled={busy} className="h-8 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{t('pfw_members.register_company', 'Register Company')}</button>)}
          </div>
        </div>

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <span className="mr-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('pfw_members.pending_heading', 'Pending Members')}</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('pfw_members.filter_placeholder', 'Filter members…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('pfw_members.col.company', 'Company')} options={facetOptions.company} selected={facets.company} onChange={v => setFacets(f => ({ ...f, company: v }))} />
            <FacetFilter label={t('pfw_members.col.member_role', 'Role')} options={facetOptions.role} selected={facets.role} onChange={v => setFacets(f => ({ ...f, role: v }))} />
            <FacetFilter label={t('pfw_members.col.status', 'Status')} options={facetOptions.status} selected={facets.status} onChange={v => setFacets(f => ({ ...f, status: v }))} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('pfw_members.working', 'Working…')}</span>}
              {notice && <span className="text-csm-xs text-primary">{notice}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
        {busy && pending.length === 0 ? <SkeletonRows rows={8} /> : (
        <PersonalizedGrid componentKey="grid" columns={PENDING_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('pfw_members.col.related', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('pfw_members.col.' + col.key, col.header)}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('pfw_members.col.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(m => (
                  <Fragment key={m.pfw_member_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle">
                      <RelatedExpandButton open={detail.isOpen(m.pfw_member_id)} hasChildren={hasRelated(m.pfw_member_id)} onToggle={() => detail.toggle(m.pfw_member_id)} expandLabel={t('pfw_members.related.expand', 'Show related records')} collapseLabel={t('pfw_members.related.collapse', 'Hide related')} tooltip={t('pfw_members.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 text-csm-sm"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'company_code' && <span className="font-mono">{m.pfw_company?.internal_code ?? '—'}</span>}
                        {col.key === 'company_name' && <span className="text-csm-ink-soft">{m.pfw_company?.name}</span>}
                        {col.key === 'member_role'  && m.member_role}
                        {col.key === 'status'       && (() => {
                          const meta = STATUS_META[m.status];
                          return <StatusBadge label={meta ? t(meta.labelKey, meta.label) : m.status} variant={meta?.variant ?? 'neutral'} />;
                        })()}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right">
                      {canChange && (<button onClick={() => void decide(m.pfw_member_id, 'APPROVE')} className="mr-1 rounded border border-primary bg-primary px-2 py-0.5 text-csm-xs text-white hover:bg-primary-hover">{t('pfw_members.approve', 'Approve')}</button>)}
                      {canChange && (<button onClick={() => void decide(m.pfw_member_id, 'REJECT')} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger">{t('pfw_members.reject', 'Reject')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(m.pfw_member_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3">
                        <RelatedChildrenPanel groups={buildRelatedGroups(m.pfw_member_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="px-2 py-3">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'all-clear'} title={t('pfw_members.empty', 'Nothing awaiting approval.')} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>
        </ContentCard>
      </div>
    </div>
  );
}
